import { useEffect, useState } from "react";
import bundledManifest from "./staticManifest.web.json";

/*
 * Static images served from the assets CDN (repo: campusmesh/assets, static-cdn/).
 * The manifest maps group → name → hashed url, e.g. getStaticImage("auth", "login-background").
 * Until the manifest is loaded (or if it fails, or the key is missing) the local fallback is returned,
 * so the UI never shows a broken image.
 */

const BASE = (process.env.REACT_APP_CDN || "").replace(/\/+$/, "");
const MANIFEST_URL = `${BASE}/assets/manifest-web.json`;
const CACHE_KEY = "static-manifest-web";

// CloudFront stores these widths; ?w= picks the closest one at or above it
export const WEB_WIDTHS = [400, 800, 1200, 1600, 2400];

let manifest = null;
let loading = null;
const listeners = new Set();

const readCache = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY));
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    /* storage unavailable */
  }
};

// The manifest downloaded at build time (scripts/fetch-static-manifest.js) is bundled, so images resolve on the very
// first render. A newer cached copy or a live fetch replaces it; the live fetch needs CORS on the CDN, so it can
// fail in the browser, which is fine.
const newest = (a, b) => (!a?.images ? b : !b?.images ? a : (a.version || "") >= (b.version || "") ? a : b);

export const loadStaticManifest = () => {
  if (loading) return loading;
  const cached = BASE ? readCache() : null;
  manifest = newest(bundledManifest, cached);
  const startVersion = manifest?.version;

  const fresh = !BASE
    ? Promise.resolve(null)
    : fetch(MANIFEST_URL, { cache: "no-cache" })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.images) {
            writeCache(data);
            manifest = newest(manifest, data);
            listeners.forEach((fn) => fn());
          }
          return data;
        })
        .catch(() => null);

  loading = { fromCache: Boolean(manifest?.images && Object.keys(manifest.images).length), cachedVersion: startVersion, fresh };
  return loading;
};

export const getStaticImage = (group, name, { w, fallback = "" } = {}) => {
  const entry = manifest?.images?.[group]?.[name];
  if (!entry?.url) return fallback;
  // SVGs are not resized, and ?w= only applies to webp
  if (w && /\.webp$/i.test(entry.url)) return `${entry.url}?w=${w}`;
  return entry.url;
};

// Re-renders the component once the manifest arrives
export const useStaticImage = (group, name, options) => {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    loadStaticManifest();
    return () => listeners.delete(fn);
  }, []);
  return getStaticImage(group, name, options);
};
